import React, { useMemo } from 'react';
import { translate } from 'components/Translation';
import CustomDialog from 'components/CustomDialog/CustomDialog';
import styles from 'assets/jss/material-dashboard-pro-react/views/payments/billsStyle';
import { makeStyles } from '@material-ui/styles';
import ConditionalRender from 'components/ConditionalRender/ConditionalRender';
import { LinkCell, TextCell } from 'components/Cells/Cells';
import Table from 'components/CustomTable/CustomTable';
import moment from 'utils/date/moment';
import CustomTooltip from 'components/CustomTooltip/CustomTooltip';
import Delete from '@material-ui/icons/DeleteOutline';
import { BodyM, DisplayS } from 'components/Typography/Typography';
import Button from 'components/CustomButtons/Button';
import { roundMoney } from 'utils/converters/roundMoney';

// @ts-ignore
const useStyles = makeStyles(styles);

interface IPaySelectedProps {
	isOpen: boolean;
	hideDialog: () => void;
	unpaidInvoices: types.bills.IExtendedInvoice[];
	payAction: () => void;
	removeBillFromList: (bill: types.bills.IExtendedInvoice) => void;
}

const PaySelected = ({ isOpen, hideDialog, unpaidInvoices, payAction, removeBillFromList }: IPaySelectedProps) => {
	const classes = useStyles();
	const balance = useMemo(() => {
		return unpaidInvoices.reduce((acc, singleUnpaidInvoice) => acc + singleUnpaidInvoice.balanceDue, 0);
	}, [unpaidInvoices]);

	return (
		<>
			<CustomDialog
				isOpen={isOpen}
				hideDialog={hideDialog}
				width={736}
				header={translate('bills.selected.bills')}
				dialogBody={
					<div className={classes.paySelectedBodyWrapper}>
						<Table
							withHeader={false}
							showPaginationTop={false}
							tableProps={{
								defaultPageSize: unpaidInvoices.length,
								pageSize: unpaidInvoices.length,
								data: unpaidInvoices,
								loading: false,
								columns: [
									{
										Header: translate('payments.bills.date'),
										accessor: 'invoiceDate',
										width: 140,
										Cell: ({ original }: any) => (
											<TextCell text={moment(original.invoiceDate).format('DD.MM.YYYY')} />
										),
									},
									{
										Header: translate('payments.bills.accountNumber'),
										accessor: 'billingId',
										Cell: ({ original }: any) => (
											<LinkCell
												path={`/dashboard/active-services/${original.billingId}/${original.account.accountId}`}
												text={original.billingId}
											/>
										),
									},
									{
										Header: translate('payments.bills.totalDue'),
										accessor: 'invoiceAmount',
										width: 150,
										Cell: ({ original }: any) => {
											const alreadyPaid = original.totalAmount - original.balanceDue;
											const balanceDueFormatted = roundMoney(original.balanceDue);
											const alreadyPaidFormatted = roundMoney(alreadyPaid);
											return (
												<div className={classes.totalDueCell}>
													<BodyM inline disableMargin>
														{translate('price', balanceDueFormatted)}
													</BodyM>
													<ConditionalRender
														show={alreadyPaid > 0}
														onTrue={() => (
															<div className={classes.totalDueTooltipWrapper}>
																<CustomTooltip placement="top" size="sm">
																	{translate(
																		'bills.list.total.due.tooltip',
																		alreadyPaidFormatted
																	)}
																</CustomTooltip>
															</div>
														)}
													/>
												</div>
											);
										},
									},
									{
										id: 'interactiveBill',
										width: 50,
										Cell: ({ original }: any) => (
											<div className={classes.document}>
												<Button
													color="tertiary-light"
													justIcon
													onClick={() => removeBillFromList(original)}
												>
													<Delete />
												</Button>
											</div>
										),
									},
								],
							}}
						/>
						<div className={classes.paySelectedSummary}>
							<BodyM>{translate('payments.topup.totalAmount')}</BodyM>
							<span>
								<DisplayS disableMargin inline>
									{`${roundMoney(balance)} ${translate('OMR')}`}
								</DisplayS>
							</span>
						</div>
					</div>
				}
				rightSideButtons={[
					{
						text: translate('cancel'),
						color: 'tertiary-light',
						onClick: hideDialog,
					},
					{
						text: translate('bills.pay.balance.pay.now'),
						color: 'primary-light',
						onClick: payAction,
						disabled: unpaidInvoices.length === 0,
					},
				]}
			/>
		</>
	);
};

export default PaySelected;
